import {
  useGetUploadSignedUrlForTaskQuestionLazyQuery,
  useGetDownloadSignedUrlForTaskQuestionLazyQuery,
} from '@exam-management/graphql';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';

/**
 * A hook to upload an image to Google Cloud Storage for a question review task.
 *
 * @param params - An optional object with the following properties:
 *   - `taskId`: The ID of the question review task to upload the image for.
 *   - `questionId`: The ID of the question to upload the image for.
 *   - `options`: An optional object with the following properties:
 *     - `mode`: The mode of the upload, either 'no-download' or 'download'. 'no-download' | 'download' - 'no-download' will return a base64 encoded image, 'download' will return a signed URL.
 *     - `fileSizeLimit`: The maximum file size limit in bytes for the uploaded image.
 */
export function useUploadImageForQuestionReviewTask(params: {
  taskId: string;
  questionId: string;
  options?: {
    mode?: 'no-download' | 'download';
    fileSizeLimit?: number;
  };
}) {
  const { taskId, questionId, options } = params ?? {};
  const { mode = 'no-download', fileSizeLimit = 250000 } = options ?? {};
  const { showSnackbar } = useSnackbar();
  const logger = useLogger();
  const [getUploadSignedUrl] = useGetUploadSignedUrlForTaskQuestionLazyQuery({
    fetchPolicy: 'no-cache', // avoid stale data
  });
  const [getDownloadSignedUrl] = useGetDownloadSignedUrlForTaskQuestionLazyQuery();
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Uploads image to Google Cloud Storage using a signed URL
   *
   * @param file Image to upload. Allowed types: .png, .jpeg, .jpg
   * @throws {Error} If the file type is invalid, or if the upload or fetch fails
   */
  const handleUpload = async (file: File) => {
    try {
      if (!file || !['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        showSnackbar({
          variant: 'error',
          message: 'กรุณาใช้ไฟล์รูปภาพเท่านั้น',
        });
        logger.error('Invalid file type! Please use .png, .jpeg, or .jpg');
        return;
      }

      // Check file size limit (default is 250kb)
      if (file.size > fileSizeLimit) {
        showSnackbar({
          variant: 'error',
          message: `กรุณาอัปโหลดไฟล์รูปที่มีขนาดไม่เกิน ${fileSizeLimit / 1000} KB`,
        });
        logger.error(
          `File size is too large! Please use a file smaller than ${fileSizeLimit / 1000} KB`,
        );
        return;
      }

      setIsLoading(true);

      const { data: getUploadSignedUrlData } = await getUploadSignedUrl({
        variables: {
          input: {
            contentType: file.type,
            questionId,
          },
          taskId,
        },
        onError: (error) => {
          throw new Error(error.message);
        },
      });

      if (!getUploadSignedUrlData) {
        throw new Error('No data is uploaded to Google Cloud Storage');
      }

      const {
        signedUrl,
        storageObject: { fileName: _fileName },
      } = getUploadSignedUrlData.qctGetUploadSignedUrlForTaskQuestion;

      const fileName = _fileName as string;

      const uploadFileResponse = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadFileResponse.ok) {
        throw new Error('upload file failed');
      }

      if (mode === 'no-download') {
        const reader = new FileReader();
        return new Promise<{ url: string; fileName: string }>((resolve) => {
          reader.onload = (readerEvent) => {
            const url = (readerEvent.target as FileReader).result as string;
            resolve({
              url,
              fileName,
            });
          };
          reader.readAsDataURL(file);
        });
      }

      const { data: getDownloadSignedUrlData } = await getDownloadSignedUrl({
        variables: {
          input: {
            fileNames: [fileName],
            questionId: questionId,
          },
          taskId,
        },
        onError: (error) => {
          throw new Error(error.message);
        },
      });

      if (!getDownloadSignedUrlData) {
        throw new Error('Data is not found in Google Cloud Storage');
      }

      return {
        url: getDownloadSignedUrlData.qctGetDownloadSignedUrlForTaskQuestion.signedUrls[
          fileName
        ] as string,
        fileName,
      };
    } catch (error) {
      showSnackbar({ variant: 'error', message: 'เกิดข้อผิดพลาดในการอัปโหลดไฟล์' });
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return { handleUpload, loading: isLoading };
}
